import type { PayInResponse } from '@/types/PayInResponse';
import type { RouteTransfer } from '@/types/RouteTransferType';
import {
  INSWITCH_ALLOWED_BY_CURRENCY,
  INSWITCH_METHOD_NAMES,
  INSWITCH_PROHIBITED_COUNTRIES,
  INSWITCH_RESTRICTED_ACTIVITY_IDS,
  INSWITCH_PAY_IN_REFERENCE_FIELDS,
} from '../constants/inswitch.constant';
import type { B2bPaymentMethodWithLimits } from '../types/inswitch.type';

/**
 * Extrae la referencia de pago Inswitch desde la respuesta del pay-in,
 * iterando los campos definidos en INSWITCH_PAY_IN_REFERENCE_FIELDS en orden de prioridad.
 * @param payIn - Respuesta del servicio pay-in.
 * @returns La primera referencia no vacía encontrada, o null si no existe ninguna.
 */
export function pickInswitchPayInReference(payIn: PayInResponse): string | null {
  for (const key of INSWITCH_PAY_IN_REFERENCE_FIELDS as (keyof PayInResponse)[]) {
    const raw = payIn[key];
    if (raw === undefined || raw === null) continue;
    const trimmed = String(raw).trim();
    if (trimmed !== '') return trimmed;
  }
  return null;
}

/**
 * Obtiene los límites de monto (mínimo y máximo) para Inswitch según la moneda de la billetera,
 * buscando el método de pago correspondiente dentro del listado B2B.
 * @param methods - Lista de métodos de pago con sus bandas de límites.
 * @param walletCurrencyCode - Código de moneda de la billetera activa (ej. 'COP', 'CLP').
 * @returns Objeto con `min` y `max` en la moneda indicada, o null si no se encontró el método o los límites.
 */
export function getInswitchAmountLimitsFromB2bPaymentMethods(
  methods: B2bPaymentMethodWithLimits[],
  walletCurrencyCode: string
): { min: number; max: number } | null {
  const code = walletCurrencyCode.trim().toUpperCase();
  const methodName = INSWITCH_METHOD_NAMES.find((n) => n.endsWith(code));
  if (!methodName) return null;

  const method = methods.find((m) => m.name === methodName);

  const limits = method?.limits;
  if (!limits?.length) return null;

  const band = limits.find((l) => l.currencyCode?.trim().toUpperCase() === code);
  if (!band) return null;

  const min = band.amountMin;
  const max = band.amountMax;
  if (min == null || max == null) return null;

  return { min, max };
}

/**
 * Determina si la billetera del usuario puede usar Inswitch según su moneda y si es la cuenta principal.
 * @param walletCurrency - Código de moneda de la billetera (ej. 'COP', 'CLP').
 * @param isMainWallet - true si la billetera es la cuenta principal del usuario.
 * @returns true solo si la moneda está en INSWITCH_ALLOWED_BY_CURRENCY y la billetera es la cuenta principal.
 */
export function canUseInswitchForWallet(
  walletCurrency: string,
  isMainWallet: boolean
): boolean {
  if (isMainWallet && INSWITCH_ALLOWED_BY_CURRENCY.includes(walletCurrency)) return true;
  return false;
}

// Acepta escalar o array de ids para mantener retro-compat con call
// sites legacy (`activityId: number`) sin bifurcar la firma. Basta con
// que UN id esté restringido para bloquear al bloque completo.
export function canUseInswitchForCompany(
  countryCode: string,
  activityIds?: number | number[] | null
): boolean {
  if (INSWITCH_PROHIBITED_COUNTRIES.includes(countryCode)) return false;
  if (activityIds == null) return true;
  const ids = Array.isArray(activityIds) ? activityIds : [activityIds];
  if (ids.length === 0) return true;
  return !ids.some((id) => INSWITCH_RESTRICTED_ACTIVITY_IDS.includes(id));
}

/**
 * Indica si el nombre de método de pago corresponde a un método Inswitch.
 * Valida que el nombre contenga la palabra "INSWITCH" sin importar el sufijo de moneda.
 * @param name - Nombre del método de pago (ej. 'INSWITCHCOP', 'INSWITCHCLP').
 * @returns true si el nombre contiene "INSWITCH".
 */
export function isInswitchMethod(name?: string): boolean {
  if (!name) return false;
  return name.toUpperCase().includes('INSWITCH');
}

/**
 * Indica si el nombre de un método de pago corresponde a InSwitch para una moneda concreta.
 * Exige que el nombre contenga "INSWITCH" y además termine con el código de moneda,
 * que es la convención con la que el backend nombra los métodos (ej. 'INSWITCHCOP').
 * @param name - Nombre del método de pago recibido en el payload de rutas.
 * @param currency - Código de moneda de la billetera a validar (ej. 'COP', 'CLP').
 * @returns true solo si el nombre es InSwitch y su sufijo coincide con la moneda indicada.
 */
export function isInswitchMethodForCurrency(name: string | undefined, currency: string): boolean {
  if (!isInswitchMethod(name)) return false;
  const normalizedCurrency = currency.trim().toUpperCase();
  if (normalizedCurrency === '') return false;
  return (name ?? '').toUpperCase().endsWith(normalizedCurrency);
}

/**
 * Verifica si el backend habilitó Links de Cobro para una moneda concreta.
 * Requiere que el método InSwitch cumpla las 4 condiciones: el nombre del método
 * termina con el sufijo de la moneda consultada, `thirdPartyPayment === 'BOTH'`,
 * `isEnabled === true` e `isB2BMethod === true`.
 * @param routes - Lista de rutas de transferencia del store.
 * @param currency - Código de moneda a verificar (ej. 'COP', 'CLP').
 * @returns true si existe al menos un método InSwitch habilitado para Links de Cobro en esa moneda.
 */
export function isPaymentLinksCurrencyEnabled(routes: RouteTransfer[], currency: string): boolean {
  return routes.some(
    (route) =>
      route.currency.code === currency &&
      route.paymentMethods.some(
        (method) =>
          isInswitchMethodForCurrency(method.name, currency) &&
          method.thirdPartyPayment === 'BOTH' &&
          method.isEnabled === true &&
          method.isB2BMethod === true
      )
  );
}
